import { HStack, Text } from '@cad-arc';
import { some, uniq } from 'lodash';
import { CliArgumentConfigurationView } from '@mark43/cad-api';
import React from 'react';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { SegmentTypeEnumType, segmentTypes } from './constants';
import { CommandLineConfigView } from './helpers';
import { CommandSegment } from './types';

const FORM_IDENTIFIER_SEGMENTS = {
    FORM_IDENTIFIER: true,
    [segmentTypes.VEHICLE]: true,
    [segmentTypes.DISPATCH_AREA]: true,
    [segmentTypes.DISPATCH_AREA_GROUP]: true,
    [segmentTypes.AGENCY_EVENT_RADIO_CHANNELS]: true,
    [segmentTypes.MEMBER]: true,
    [segmentTypes.AGENCY]: true,
    [segmentTypes.CALL_FOR_SERVICE]: true,
    [segmentTypes.DISPOSITION]: true,
    [segmentTypes.STATION]: true,
    [segmentTypes.ROTATION_SERVICE_TYPE]: true,
    [segmentTypes.MESSAGE_GROUP]: true,
    [segmentTypes.UNIT_TAG]: true,
    [segmentTypes.CALL_TAKER_STATION]: true,
    [segmentTypes.HOSPITAL]: true,
    [segmentTypes.MESSAGE_RECIPIENT]: true,
};

const FieldHint: React.FC<{
    isTruncated?: boolean;
    required: boolean;
    children?: React.ReactNode;
}> = ({ required, children, isTruncated }) => {
    return (
        <Text isTruncated={isTruncated} variant="bodyMd" style={{ opacity: required ? 1 : 0.2 }}>
            {children}
        </Text>
    );
};

export function CommandLineHints({
    activeCommandSegment,
    config,
}: {
    activeCommandSegment?: CommandSegment;
    config: CommandLineConfigView;
}): React.ReactElement {
    const enabledFeatures = useEnabledFeatures();
    const {
        tokens: {
            dexInquiryParamsToken,
            eventToken,
            locationToken,
            licensePlateToken,
            highPriorityToken,
            formFieldSeparator,
            formFieldDelimiter,
        },
    } = config;

    const activeCommandSegmentTypes: SegmentTypeEnumType[] = activeCommandSegment
        ? uniq(
              (activeCommandSegment.segmentData as CliArgumentConfigurationView).cliArgumentConfigurationLinks.map(
                  (link) => link.cliArgument as SegmentTypeEnumType
              )
          )
        : [];

    const requiredSegmentTypes = activeCommandSegmentTypes ?? [];

    const dexCommandParams = activeCommandSegment?.segmentData?.dexCommandParams ?? [];

    const dexInquiryHintInner =
        dexCommandParams.length > 0
            ? dexCommandParams.map((d) => d.name).join(dexInquiryParamsToken)
            : ['inquiry', 'params'].join(dexInquiryParamsToken);
    const dexInquiryHint = `${dexInquiryParamsToken}${dexInquiryHintInner}${dexInquiryParamsToken}`;

    return (
        <HStack px="4" style={{ marginLeft: 'auto' }} isTruncated>
            <FieldHint required={requiredSegmentTypes.includes('UNIT')}>units</FieldHint>
            <FieldHint required={requiredSegmentTypes.includes('EVENT')}>
                {eventToken}
                event
            </FieldHint>

            <FieldHint required={requiredSegmentTypes.includes('LOCATION')}>
                {locationToken}
                location
                {locationToken}
            </FieldHint>
            <FieldHint required={requiredSegmentTypes.includes('COMMENT')}>,comment</FieldHint>
            <FieldHint required={requiredSegmentTypes.includes('LICENSE_PLATE')}>
                {licensePlateToken}
                plate
                {licensePlateToken}
            </FieldHint>
            <FieldHint required={requiredSegmentTypes.includes('IS_HIGH_PRIORITY')}>
                {highPriorityToken}
            </FieldHint>
            <FieldHint
                required={some(
                    activeCommandSegmentTypes,
                    (segmentType) => (FORM_IDENTIFIER_SEGMENTS as any)[segmentType]
                )}
            >
                form
                {formFieldSeparator}
                value(
                {formFieldDelimiter})
            </FieldHint>
            {enabledFeatures.dataExchange && dexInquiryParamsToken && (
                <FieldHint
                    isTruncated
                    required={requiredSegmentTypes.includes('DATA_EXCHANGE_INQUIRY_PARAMETERS')}
                >
                    {dexInquiryHint}
                </FieldHint>
            )}
        </HStack>
    );
}
